import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Bell, Save, Loader2, Settings2, CheckCircle2, Clock, Calendar,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";

type ReminderTemplate = {
  id: string;
  nom: string;
  description: string | null;
  triggerEvent: string;
  delayDays: number;
  messageTemplate: string;
  priorite: string;
  actif: boolean;
  categorie: string;
  variablesDisponibles: string[];
};

const CATEGORIE_LABELS: Record<string, string> = {
  client:     "Clients",
  prospect:   "Prospects",
  revendeur:  "Revendeurs",
  mission:    "Missions",
  maintenance: "Maintenance",
  ticket:     "Tickets",
  devis:      "Devis",
  general:    "Général",
};

const PRIORITE_CONFIG: Record<string, { label: string; color: string }> = {
  basse:   { label: "Basse",   color: "bg-slate-100 text-slate-600" },
  moyenne: { label: "Moyenne", color: "bg-blue-100 text-blue-700" },
  haute:   { label: "Haute",   color: "bg-orange-100 text-orange-700" },
};

export default function ParametresRappels() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<ReminderTemplate | null>(null);
  const [editForm, setEditForm] = useState({ nom: "", description: "", delayDays: 0, messageTemplate: "", actif: true });

  const { data: templates = [], isLoading } = useQuery<ReminderTemplate[]>({
    queryKey: ["/api/reminder-templates"],
    queryFn: async () => (await authFetch("/api/reminder-templates")).json(),
  });

  const saveMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, any> }) => {
      return (await authFetch(`/api/reminder-templates/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })).json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reminder-templates"] });
      toast({ title: "Modèle mis à jour" });
      setEditingTemplate(null);
    },
    onError: () => toast({ variant: "destructive", title: "Erreur de sauvegarde" }),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, actif }: { id: string; actif: boolean }) => {
      return (await authFetch(`/api/reminder-templates/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ actif }),
      })).json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/reminder-templates"] }),
  });

  const openEdit = (tpl: ReminderTemplate) => {
    setEditForm({
      nom: tpl.nom,
      description: tpl.description || "",
      delayDays: tpl.delayDays,
      messageTemplate: tpl.messageTemplate,
      actif: tpl.actif,
    });
    setEditingTemplate(tpl);
  };

  const groupedTemplates = templates.reduce<Record<string, ReminderTemplate[]>>((acc, tpl) => {
    const cat = tpl.categorie || "general";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(tpl);
    return acc;
  }, {});

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <Link href="/parametres" className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2" data-testid="text-page-title">
            <Bell className="h-6 w-6 text-primary" />
            Configuration des rappels
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Activez et personnalisez les modèles de rappels automatiques
          </p>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 rounded-lg p-4 text-sm text-blue-800 dark:text-blue-300">
        <p className="font-medium mb-1">Comment fonctionnent les rappels automatiques ?</p>
        <p className="text-blue-700 dark:text-blue-400">
          Les rappels sont générés automatiquement chaque nuit via un job planifié.
          Vous pouvez activer/désactiver chaque modèle et personnaliser le délai et le message.
          Les variables <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded text-xs">{`{{variable}}`}</code> sont remplacées dynamiquement.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedTemplates).map(([cat, catTemplates]) => (
            <div key={cat}>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {CATEGORIE_LABELS[cat] || cat}
              </h2>
              <div className="space-y-2">
                {catTemplates.map(tpl => {
                  const prioriteConf = PRIORITE_CONFIG[tpl.priorite] || PRIORITE_CONFIG.moyenne;
                  return (
                    <div
                      key={tpl.id}
                      className={cn(
                        "border rounded-xl p-4 transition-all",
                        tpl.actif ? "bg-card border-border" : "bg-muted/30 border-border/50 opacity-70"
                      )}
                      data-testid={`template-card-${tpl.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-medium text-sm" data-testid={`text-template-nom-${tpl.id}`}>
                              {tpl.nom}
                            </span>
                            <Badge variant="outline" className={cn("text-[10px] px-1.5", prioriteConf.color)}>
                              {prioriteConf.label}
                            </Badge>
                            <Badge variant="outline" className="text-[10px] px-1.5 text-muted-foreground">
                              J+{tpl.delayDays} jours
                            </Badge>
                          </div>
                          {tpl.description && (
                            <p className="text-xs text-muted-foreground mb-2">{tpl.description}</p>
                          )}
                          <p className="text-xs bg-muted/50 rounded px-2 py-1.5 font-mono text-muted-foreground">
                            {tpl.messageTemplate}
                          </p>
                          {tpl.variablesDisponibles && tpl.variablesDisponibles.length > 0 && (
                            <div className="flex gap-1 flex-wrap mt-2">
                              {tpl.variablesDisponibles.map(v => (
                                <code key={v} className="text-[10px] bg-muted px-1 py-0.5 rounded text-muted-foreground">
                                  {`{{${v}}}`}
                                </code>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs"
                            onClick={() => openEdit(tpl)}
                            data-testid={`btn-edit-template-${tpl.id}`}
                          >
                            <Settings2 className="h-3.5 w-3.5 mr-1" />
                            Modifier
                          </Button>
                          <Switch
                            checked={tpl.actif}
                            onCheckedChange={(checked) => toggleMutation.mutate({ id: tpl.id, actif: checked })}
                            data-testid={`switch-template-${tpl.id}`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={open => !open && setEditingTemplate(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              Modifier le modèle
            </DialogTitle>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label>Nom</Label>
                <Input
                  value={editForm.nom}
                  onChange={e => setEditForm(prev => ({ ...prev, nom: e.target.value }))}
                  data-testid="input-template-nom"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Description (interne)</Label>
                <Input
                  value={editForm.description}
                  onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Description optionnelle..."
                  data-testid="input-template-description"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Délai (jours après l'événement déclencheur)</Label>
                <Input
                  type="number"
                  min={0}
                  max={365}
                  value={editForm.delayDays}
                  onChange={e => setEditForm(prev => ({ ...prev, delayDays: parseInt(e.target.value) || 0 }))}
                  data-testid="input-template-delay"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Message du rappel</Label>
                <Textarea
                  value={editForm.messageTemplate}
                  onChange={e => setEditForm(prev => ({ ...prev, messageTemplate: e.target.value }))}
                  rows={3}
                  placeholder="Texte du rappel avec variables {{variable}}..."
                  data-testid="input-template-message"
                />
                {editingTemplate.variablesDisponibles && editingTemplate.variablesDisponibles.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Variables : {editingTemplate.variablesDisponibles.map(v => `{{${v}}}`).join(", ")}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={editForm.actif}
                  onCheckedChange={v => setEditForm(prev => ({ ...prev, actif: v }))}
                  data-testid="switch-template-actif"
                />
                <Label>Modèle actif</Label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingTemplate(null)}>Annuler</Button>
                <Button
                  onClick={() => saveMutation.mutate({
                    id: editingTemplate.id,
                    data: editForm,
                  })}
                  disabled={saveMutation.isPending}
                  data-testid="btn-save-template"
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
